import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { DayPicker, type DateRange } from "react-day-picker";
import { format } from "date-fns";
import { enUS, srLatn } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";

// ─────────────────────────────────────────────
// DatePicker
//
// Calendar + Popover pattern. ISO 8601 display.
// Design: §6.12 Date Picker
// ─────────────────────────────────────────────

// ── Calendar ─────────────────────────────────
// Class keys target react-day-picker v9 markup (v8 keys like `caption`/
// `head_row` no longer exist there and left the grid unstyled/broken).
type CalendarProps = React.ComponentPropsWithoutRef<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const { language } = useTranslation();
  return (
    <DayPicker
      locale={language === "sr" ? srLatn : enUS}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "relative flex flex-col sm:flex-row gap-4",
        month: "flex w-full flex-col gap-3",
        month_caption: "flex h-7 items-center justify-center",
        caption_label: "text-[14px] font-medium capitalize text-text-primary",
        nav: "absolute inset-x-1 top-0 z-10 flex items-center justify-between",
        button_previous: cn(
          "h-7 w-7 bg-transparent p-0 rounded-control text-text-muted",
          "hover:bg-hover hover:text-text-secondary transition-colors",
          "inline-flex items-center justify-center"
        ),
        button_next: cn(
          "h-7 w-7 bg-transparent p-0 rounded-control text-text-muted",
          "hover:bg-hover hover:text-text-secondary transition-colors",
          "inline-flex items-center justify-center"
        ),
        chevron: "h-4 w-4 fill-current",
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "w-8 text-[0.8rem] font-normal capitalize text-text-muted",
        week: "mt-2 flex w-full",
        day: cn(
          "relative h-8 w-8 p-0 text-center text-[14px]",
          "focus-within:relative focus-within:z-20"
        ),
        day_button: cn(
          "h-8 w-8 p-0 font-normal rounded-control text-text-secondary",
          "hover:bg-hover hover:text-text-primary transition-colors",
          "inline-flex items-center justify-center"
        ),
        selected: cn(
          "rounded-control bg-accent",
          "[&>button]:text-white [&>button]:hover:bg-accent [&>button]:hover:text-white"
        ),
        today: "rounded-control bg-hover font-medium [&>button]:text-text-primary",
        outside: "opacity-50 [&>button]:text-text-muted",
        disabled: "opacity-30 [&>button]:text-text-muted [&>button]:hover:bg-transparent",
        hidden: "invisible",
        range_start: "rounded-l-control rounded-r-none bg-accent [&>button]:text-white",
        range_end: "rounded-r-control rounded-l-none bg-accent [&>button]:text-white",
        range_middle: "rounded-none bg-accent-bg [&>button]:text-text-primary",
        ...classNames,
      }}
      {...props}
    />
  );
}

// ── DatePicker (single date selection) ───────
interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  label?: string;
  placeholder?: string;
  errorText?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  fromDate?: Date;
  toDate?: Date;
}

function DatePicker({
  value,
  onChange,
  label,
  placeholder,
  errorText,
  helperText,
  required,
  disabled,
  className,
  fromDate,
  toDate,
}: DatePickerProps) {
  const { t } = useTranslation();
  const placeholderText = placeholder ?? t("common.pickDate");
  return (
    <Popover>
      <div className={cn("flex flex-col gap-[6px]", className)}>
        {label && (
          <label className="text-[14px] font-medium text-text-secondary">
            {label}
            {required && <span className="ml-1 text-danger">*</span>}
          </label>
        )}
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-control border bg-bg-input px-3 py-2",
              "text-[14px] transition-colors focus:outline-none focus:ring-1",
              errorText
                ? "border-danger focus:border-danger focus:ring-danger"
                : "border-border-default focus:border-accent focus:ring-accent",
              !value && "text-text-muted",
              value && "text-text-primary",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            <span>{value ? format(value, "yyyy-MM-dd") : placeholderText}</span>
            <CalendarIcon className="h-4 w-4 flex-shrink-0 text-text-muted" />
          </button>
        </PopoverTrigger>

        {errorText && (
          <p className="text-[12px] text-danger">{errorText}</p>
        )}
        {!errorText && helperText && (
          <p className="text-caption text-text-muted">{helperText}</p>
        )}
      </div>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          disabled={(date) => {
            if (fromDate && date < fromDate) return true;
            if (toDate && date > toDate) return true;
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// ── DateRangePicker ──────────────────────────
interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  label?: string;
  placeholder?: string;
  errorText?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  maxDays?: number;
}

function DateRangePicker({
  value,
  onChange,
  label,
  placeholder,
  errorText,
  helperText,
  required,
  disabled,
  className,
  maxDays,
}: DateRangePickerProps) {
  const { t } = useTranslation();
  const displayText =
    value?.from != null
      ? value.to != null
        ? `${format(value.from, "yyyy-MM-dd")} - ${format(value.to, "yyyy-MM-dd")}`
        : format(value.from, "yyyy-MM-dd")
      : placeholder ?? t("common.pickDateRange");

  return (
    <Popover>
      <div className={cn("flex flex-col gap-[6px]", className)}>
        {label && (
          <label className="text-[14px] font-medium text-text-secondary">
            {label}
            {required && <span className="ml-1 text-danger">*</span>}
          </label>
        )}
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-control border bg-bg-input px-3 py-2",
              "text-[14px] transition-colors focus:outline-none focus:ring-1",
              errorText
                ? "border-danger focus:border-danger focus:ring-danger"
                : "border-border-default focus:border-accent focus:ring-accent",
              !value?.from && "text-text-muted",
              value?.from && "text-text-primary",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            <span className="truncate" style={{ fontFamily: "inherit" }}>{displayText}</span>
            <CalendarIcon className="h-4 w-4 flex-shrink-0 text-text-muted" />
          </button>
        </PopoverTrigger>

        {errorText && (
          <p className="text-[12px] text-danger">{errorText}</p>
        )}
        {!errorText && helperText && (
          <p className="text-caption text-text-muted">{helperText}</p>
        )}
      </div>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={onChange}
          numberOfMonths={2}
          max={maxDays}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export { Calendar, DatePicker, DateRangePicker };
